import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Progress } from "./ui/progress";
import {
  ChevronRight,
  ChevronLeft,
  Check,
  Sparkles,
  Lightbulb,
  AlertCircle,
  HelpCircle,
  RefreshCw,
  Save,
  Send,
  X,
  CheckCircle2,
  Info,
  Zap,
} from "lucide-react";

/**
 * TurboTax-style Wizard Component with Inline AI Assistance
 * 
 * Usage:
 * <WizardContainer
 *   title="Business Impact Analysis"
 *   description="Assess the impact of disruptions on your business processes"
 *   steps={[
 *     { id: 1, title: "Process Info", description: "Basic process details", icon: FileText },
 *     { id: 2, title: "Dependencies", description: "IT systems and resources", icon: Link },
 *     ...
 *   ]}
 *   currentStep={currentStep}
 *   onStepChange={setCurrentStep}
 *   onComplete={handleSubmit}
 *   isLoading={isLoading}
 * >
 *   <WizardStep step={1}>
 *     <AIInlineSuggestion
 *       suggestion="Based on your industry (Financial Services), payment processing is typically a critical process."
 *       onApply={() => setFormData({ ...formData, criticality: "critical" })}
 *     />
 *     // Your form fields here
 *   </WizardStep>
 * </WizardContainer>
 */

// Progress Bar with Step Indicators
export function WizardProgress({ steps, currentStep, onStepClick }) {
  const progressPercentage = ((currentStep - 1) / (steps.length - 1)) * 100;
  
  return (
    <div className="mb-8">
      {/* Progress Bar */}
      <div className="relative mb-4">
        <Progress value={progressPercentage} className="h-2" />
      </div>
      
      {/* Step Indicators */}
      <div className="flex justify-between">
        {steps.map((step, index) => {
          const stepNumber = index + 1;
          const isCompleted = stepNumber < currentStep;
          const isCurrent = stepNumber === currentStep;
          const StepIcon = step.icon;
          
          return (
            <button
              key={step.id}
              onClick={() => onStepClick && stepNumber <= currentStep && onStepClick(stepNumber)}
              disabled={stepNumber > currentStep}
              className={`flex flex-col items-center transition-all ${
                stepNumber <= currentStep ? "cursor-pointer" : "cursor-not-allowed opacity-50"
              }`}
            >
              <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-all ${
                isCompleted
                  ? "bg-emerald-500 text-white"
                  : isCurrent
                  ? "bg-sky-700 text-white ring-4 ring-indigo-100"
                  : "bg-slate-200 text-slate-400"
              }`}>
                {isCompleted ? (
                  <Check className="w-5 h-5" />
                ) : StepIcon ? (
                  <StepIcon className="w-5 h-5" />
                ) : (
                  <span className="font-semibold">{stepNumber}</span>
                )}
              </div>
              <div className="mt-2 text-center">
                <p className={`text-xs font-medium ${isCurrent ? "text-sky-700" : "text-slate-500"}`}>
                  {step.title}
                </p>
                {step.clause && (
                  <Badge variant="outline" className="text-[9px] px-1 py-0 mt-0.5 bg-slate-100 text-slate-600 border-slate-300">
                    ISO {step.clause}
                  </Badge>
                )}
                {step.description && !step.clause && (
                  <p className="text-[10px] text-slate-400 hidden sm:block max-w-[80px] truncate">
                    {step.description}
                  </p>
                )}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// AI Inline Suggestion Component
export function AIInlineSuggestion({ 
  suggestion, 
  onApply, 
  onDismiss, 
  type = "suggestion", // suggestion, warning, info, tip
  isLoading = false,
  actionLabel = "Apply Suggestion"
}) {
  const [dismissed, setDismissed] = useState(false);
  
  if (dismissed) return null;
  
  const config = {
    suggestion: {
      icon: Sparkles,
      bgColor: "bg-gradient-to-r from-indigo-50 to-purple-50",
      borderColor: "border-sky-200",
      iconColor: "text-sky-700",
      titleColor: "text-sky-900",
      title: "AI Suggestion",
    },
    warning: {
      icon: AlertCircle,
      bgColor: "bg-amber-50",
      borderColor: "border-amber-200",
      iconColor: "text-amber-600",
      titleColor: "text-amber-900",
      title: "AI Warning",
    },
    info: {
      icon: Info,
      bgColor: "bg-blue-50",
      borderColor: "border-blue-200",
      iconColor: "text-blue-600",
      titleColor: "text-blue-900",
      title: "AI Insight",
    },
    tip: {
      icon: Lightbulb,
      bgColor: "bg-emerald-50",
      borderColor: "border-emerald-200",
      iconColor: "text-emerald-600",
      titleColor: "text-emerald-900",
      title: "Pro Tip",
    },
  };
  
  const { icon: Icon, bgColor, borderColor, iconColor, titleColor, title } = config[type];
  
  return (
    <div className={`${bgColor} ${borderColor} border rounded-xl p-4 mb-4 animate-fade-in`}>
      <div className="flex items-start gap-3">
        <div className={`p-2 rounded-lg ${bgColor} ${iconColor}`}>
          {isLoading ? (
            <RefreshCw className="w-5 h-5 animate-spin" />
          ) : (
            <Icon className="w-5 h-5" />
          )}
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <h4 className={`font-semibold text-sm ${titleColor}`}>{title}</h4>
            <Badge variant="outline" className="text-[10px] px-1.5 py-0">
              <Zap className="w-3 h-3 mr-1" />
              AI-Powered
            </Badge>
          </div>
          <p className="text-sm text-slate-700">{suggestion}</p>
          
          {(onApply || onDismiss) && (
            <div className="flex items-center gap-2 mt-3">
              {onApply && (
                <Button
                  size="sm"
                  onClick={onApply}
                  className="bg-sky-700 hover:bg-sky-800"
                >
                  <CheckCircle2 className="w-3 h-3 mr-1" />
                  {actionLabel}
                </Button>
              )}
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  setDismissed(true);
                  onDismiss && onDismiss();
                }}
              >
                <X className="w-3 h-3 mr-1" />
                Dismiss
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// AI Auto-Fill Button
export function AIAutoFillButton({ onAutoFill, isLoading = false, label = "Auto-fill with AI" }) {
  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      onClick={onAutoFill}
      disabled={isLoading}
      className="border-sky-200 text-sky-700 hover:bg-sky-50"
    >
      {isLoading ? (
        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
      ) : (
        <Sparkles className="w-4 h-4 mr-2" />
      )}
      {label}
    </Button>
  );
}

// Help Tooltip for fields
export function FieldHelp({ children }) {
  const [isOpen, setIsOpen] = useState(false);
  
  return (
    <div className="relative inline-block">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="text-slate-400 hover:text-slate-600 transition-colors"
      >
        <HelpCircle className="w-4 h-4" />
      </button>
      
      {isOpen && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />
          <div className="absolute z-50 w-64 p-3 bg-slate-900 text-white text-sm rounded-lg shadow-lg -top-2 left-6">
            <div className="absolute w-2 h-2 bg-slate-900 transform rotate-45 -left-1 top-3" />
            {children}
          </div>
        </>
      )}
    </div>
  );
}

// Wizard Step Container
export function WizardStep({ children, step, currentStep, className = "" }) {
  if (step !== currentStep) return null;
  
  return (
    <div className={`animate-fade-in ${className}`} data-testid={`wizard-step-${step}`}>
      {children}
    </div>
  );
}

// Step Summary Card (for review step)
export function StepSummaryCard({ title, icon: Icon, items, onEdit }) {
  return (
    <Card className="border-slate-200">
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            {Icon && <Icon className="w-4 h-4 text-sky-700" />}
            <h4 className="font-semibold text-slate-900">{title}</h4>
          </div>
          {onEdit && (
            <Button variant="ghost" size="sm" onClick={onEdit}>
              Edit
            </Button>
          )}
        </div>
        <div className="space-y-2">
          {items.map((item, index) => (
            <div key={index} className="flex justify-between text-sm">
              <span className="text-slate-500">{item.label}</span>
              <span className="font-medium text-slate-900">{item.value || "-"}</span>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

// Main Wizard Container
export function WizardContainer({
  title,
  description,
  steps,
  currentStep,
  onStepChange,
  onComplete,
  onSaveDraft,
  isLoading = false,
  isSaving = false,
  children,
  showSaveDraft = true,
  completionLabel = "Complete",
  badge,
}) {
  const isFirstStep = currentStep === 1;
  const isLastStep = currentStep === steps.length;
  
  const handleNext = () => {
    if (isLastStep) {
      onComplete && onComplete();
    } else {
      onStepChange(currentStep + 1);
    }
  };
  
  const handlePrevious = () => {
    if (!isFirstStep) {
      onStepChange(currentStep - 1);
    }
  };
  
  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          {badge && (
            <Badge className="bg-sky-100 text-sky-700">{badge}</Badge>
          )}
        </div>
        <h1 className="text-2xl font-bold text-slate-900">{title}</h1>
        {description && (
          <p className="text-slate-600 mt-1">{description}</p>
        )}
      </div>
      
      {/* Progress */}
      <WizardProgress
        steps={steps}
        currentStep={currentStep}
        onStepClick={onStepChange}
      />
      
      {/* Current Step Content */}
      <Card className="mb-6">
        <CardHeader className="border-b bg-slate-50/50">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-sky-700 text-white flex items-center justify-center font-semibold text-sm">
              {currentStep}
            </div>
            <div>
              <CardTitle className="text-lg">{steps[currentStep - 1]?.title}</CardTitle>
              {steps[currentStep - 1]?.description && (
                <CardDescription>{steps[currentStep - 1].description}</CardDescription>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-6">
          {children}
        </CardContent>
      </Card>
      
      {/* Navigation Footer */}
      <div className="flex items-center justify-between p-4 bg-white rounded-xl border border-slate-200 shadow-sm relative z-40">
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={handlePrevious}
            disabled={isFirstStep || isLoading}
          >
            <ChevronLeft className="w-4 h-4 mr-1" />
            Previous
          </Button>
          
          {showSaveDraft && onSaveDraft && (
            <Button
              type="button"
              variant="ghost"
              onClick={onSaveDraft}
              disabled={isSaving}
            >
              {isSaving ? (
                <RefreshCw className="w-4 h-4 mr-1 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-1" />
              )}
              Save Draft
            </Button>
          )}
        </div>
        
        <div className="flex items-center gap-3">
          <span className="text-sm text-slate-500">
            Step {currentStep} of {steps.length}
          </span>
          
          <Button
            type="button"
            onClick={handleNext}
            disabled={isLoading}
            className={`relative z-50 ${isLastStep ? "bg-emerald-600 hover:bg-emerald-700" : ""}`}
          >
            {isLoading ? (
              <RefreshCw className="w-4 h-4 mr-1 animate-spin" />
            ) : isLastStep ? (
              <>
                <Send className="w-4 h-4 mr-1" />
                {completionLabel}
              </>
            ) : (
              <>
                Next
                <ChevronRight className="w-4 h-4 ml-1" />
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}

// Export all components
export default {
  WizardContainer,
  WizardStep,
  WizardProgress,
  AIInlineSuggestion,
  AIAutoFillButton,
  FieldHelp,
  StepSummaryCard,
};
